import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useNavigate, useSearchParams } from "react-router-dom"
import SettingsModuleScaffold from "./SettingsModuleScaffold"
import { useActiveClinic } from "../../context/ClinicContext"

const tabs = [
  { key: "hours", label: "Horarios" },
  { key: "breaks", label: "Bloqueos y descansos" },
  { key: "availability", label: "Disponibilidad" },
  { key: "rules", label: "Reglas de agenda" }
]

const WEEK_DAYS = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" }
]

function getDefaultState() {
  return {
    hours: {
      monday: { enabled: true, start: "09:00", end: "18:00" },
      tuesday: { enabled: true, start: "09:00", end: "18:00" },
      wednesday: { enabled: true, start: "09:00", end: "18:00" },
      thursday: { enabled: true, start: "09:00", end: "18:00" },
      friday: { enabled: true, start: "09:00", end: "18:00" },
      saturday: { enabled: true, start: "09:00", end: "14:00" },
      sunday: { enabled: false, start: "09:00", end: "14:00" }
    },
    breaks: {
      lunchEnabled: false,
      lunchStart: "14:00",
      lunchEnd: "15:00",
      bufferBeforeMinutes: 0,
      bufferAfterMinutes: 0,
      blockedDatesText: "",
      notes: ""
    },
    availability: {
      slotMinutes: 30,
      sameDayCutoffHour: "18:00",
      bookingWindowDays: 60,
      respectChairCapacity: true,
      requireActiveDoctor: true,
      allowSameTimeDifferentChair: false,
      allowManualOverbook: false
    },
    rules: {
      defaultAppointmentMinutes: 30,
      emergencyBufferMinutes: 15,
      allowBackToBackLongProcedures: false,
      requireTreatmentBeforeConfirm: false,
      autoBlockLunchWhenOpen: false,
      warnWhenDoctorExceeds8Hours: true,
      notes: ""
    }
  }
}

function getStorageKey(clinicId) {
  return `odontoflow_schedule_settings_${clinicId || "default"}`
}

function parseStoredConfig(rawValue) {
  try {
    const parsed = JSON.parse(rawValue)
    return parsed && typeof parsed === "object" ? parsed : null
  } catch {
    return null
  }
}

function deepMerge(base, patch) {
  if (!patch || typeof patch !== "object") return base
  const result = { ...base }

  Object.keys(patch).forEach((key) => {
    const baseValue = base[key]
    const patchValue = patch[key]

    if (
      baseValue &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue) &&
      patchValue &&
      typeof patchValue === "object" &&
      !Array.isArray(patchValue)
    ) {
      result[key] = deepMerge(baseValue, patchValue)
    } else {
      result[key] = patchValue
    }
  })

  return result
}

function buildPreset(type) {
  const base = getDefaultState()

  if (type === "standard") {
    return base
  }

  if (type === "extended") {
    return {
      ...base,
      hours: {
        ...base.hours,
        monday: { enabled: true, start: "08:00", end: "19:00" },
        tuesday: { enabled: true, start: "08:00", end: "19:00" },
        wednesday: { enabled: true, start: "08:00", end: "19:00" },
        thursday: { enabled: true, start: "08:00", end: "19:00" },
        friday: { enabled: true, start: "08:00", end: "19:00" },
        saturday: { enabled: true, start: "09:00", end: "15:00" }
      },
      availability: {
        ...base.availability,
        bookingWindowDays: 90,
        slotMinutes: 30
      }
    }
  }

  if (type === "compact") {
    return {
      ...base,
      hours: {
        ...base.hours,
        monday: { enabled: true, start: "10:00", end: "17:00" },
        tuesday: { enabled: true, start: "10:00", end: "17:00" },
        wednesday: { enabled: true, start: "10:00", end: "17:00" },
        thursday: { enabled: true, start: "10:00", end: "17:00" },
        friday: { enabled: true, start: "10:00", end: "17:00" },
        saturday: { enabled: false, start: "09:00", end: "14:00" }
      },
      breaks: {
        ...base.breaks,
        lunchEnabled: true,
        lunchStart: "13:30",
        lunchEnd: "14:30"
      },
      availability: {
        ...base.availability,
        bookingWindowDays: 30,
        slotMinutes: 45
      }
    }
  }

  return base
}

function countEnabledDays(hours) {
  return WEEK_DAYS.filter((day) => hours?.[day.key]?.enabled).length
}

function countBlockedDates(blockedDatesText) {
  return String(blockedDatesText || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean).length
}

function getStatusLabel(savedAt) {
  if (!savedAt) return "Sin guardar todavía"
  return `Guardado ${new Date(savedAt).toLocaleString("es-MX")}`
}

export default function ScheduleAvailabilityPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClinicId } = useActiveClinic()

  const clinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : localStorage.getItem("odontoflow_clinic_id") || "default"

  const rawTab = searchParams.get("tab")
  const activeTab =
    rawTab === "breaks" || rawTab === "availability" || rawTab === "rules"
      ? rawTab
      : "hours"

  const [form, setForm] = useState(getDefaultState())
  const [savedAt, setSavedAt] = useState("")
  const [statusMessage, setStatusMessage] = useState("")
  const [statusTone, setStatusTone] = useState("neutral")
  const [loadingBackend, setLoadingBackend] = useState(false)
  const [savingBackend, setSavingBackend] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function loadScheduleAvailability() {
      const localStored = parseStoredConfig(
        localStorage.getItem(getStorageKey(clinicId))
      )

      if (!clinicId || clinicId === "default") {
        const merged = deepMerge(getDefaultState(), localStored || {})
        setForm(merged)
        setSavedAt(localStored?.meta?.savedAt || "")
        return
      }

      try {
        setLoadingBackend(true)

        const { data } = await axios.get("/api/schedule-availability", {
          params: { clinicId }
        })

        if (cancelled) return

        const backendPayload = {
          hours: data?.hours,
          breaks: data?.breaks,
          availability: data?.availability,
          rules: data?.rules,
          meta: {
            ...(data?.meta || {}),
            savedAt: data?.updatedAt || data?.meta?.savedAt || ""
          }
        }

        const merged = deepMerge(getDefaultState(), backendPayload)
        const nextSavedAt = data?.updatedAt || data?.meta?.savedAt || ""

        setForm(merged)
        setSavedAt(nextSavedAt)

        localStorage.setItem(
          getStorageKey(clinicId),
          JSON.stringify({
            ...merged,
            meta: {
              ...(merged.meta || {}),
              clinicId,
              savedAt: nextSavedAt,
              source: data?.source || "backend"
            }
          })
        )

        setStatusMessage(
          data?.source === "backend"
            ? "Configuración cargada desde backend."
            : "Usando configuración base. Guarda para crear configuración propia."
        )
        setStatusTone(data?.source === "backend" ? "success" : "info")
      } catch (err) {
        if (cancelled) return

        const merged = deepMerge(getDefaultState(), localStored || {})
        setForm(merged)
        setSavedAt(localStored?.meta?.savedAt || "")

        setStatusMessage(
          "No se pudo cargar la configuración desde backend. Se usó el respaldo local."
        )
        setStatusTone("warning")
      } finally {
        if (!cancelled) {
          setLoadingBackend(false)
        }
      }
    }

    loadScheduleAvailability()

    return () => {
      cancelled = true
    }
  }, [clinicId])

  const handleTabChange = (tab) => {
    setSearchParams({ tab })
  }

  const updateHours = (dayKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      hours: {
        ...prev.hours,
        [dayKey]: {
          ...prev.hours[dayKey],
          [field]: value
        }
      }
    }))
  }

  const updateBreaks = (field, value) => {
    setForm((prev) => ({
      ...prev,
      breaks: {
        ...prev.breaks,
        [field]: value
      }
    }))
  }

  const updateAvailability = (field, value) => {
    setForm((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [field]: value
      }
    }))
  }

  const updateRules = (field, value) => {
    setForm((prev) => ({
      ...prev,
      rules: {
        ...prev.rules,
        [field]: value
      }
    }))
  }

  const applyPreset = (presetType) => {
    const preset = buildPreset(presetType)
    setForm(preset)
    setStatusMessage("Preset aplicado. Guarda cuando lo tengas listo.")
    setStatusTone("info")
  }

  const handleSave = async () => {
    const payload = {
      ...form,
      meta: {
        ...(form.meta || {}),
        savedAt: new Date().toISOString(),
        clinicId
      }
    }

    try {
      setSavingBackend(true)

      let backendData = null

      if (clinicId && clinicId !== "default") {
        const { data } = await axios.put("/api/schedule-availability", {
          clinicId,
          hours: payload.hours,
          breaks: payload.breaks,
          availability: payload.availability,
          rules: payload.rules,
          meta: payload.meta
        })

        backendData = data
      }

      const nextSavedAt =
        backendData?.updatedAt ||
        backendData?.meta?.savedAt ||
        payload.meta.savedAt

      const localPayload = {
        ...payload,
        meta: {
          ...payload.meta,
          savedAt: nextSavedAt,
          source: backendData?.source || "local"
        }
      }

      localStorage.setItem(getStorageKey(clinicId), JSON.stringify(localPayload))
      setSavedAt(nextSavedAt)

      window.dispatchEvent(new Event("odontoflow:schedule-availability-updated"))

      setStatusMessage(
        backendData
          ? "Configuración de agenda guardada en backend correctamente."
          : "Configuración de agenda guardada localmente."
      )
      setStatusTone("success")
    } catch (err) {
      localStorage.setItem(getStorageKey(clinicId), JSON.stringify(payload))
      setSavedAt(payload.meta.savedAt)

      window.dispatchEvent(new Event("odontoflow:schedule-availability-updated"))

      setStatusMessage(
        "No se pudo guardar en backend. Se guardó respaldo local temporal."
      )
      setStatusTone("warning")
    } finally {
      setSavingBackend(false)
    }
  }

  const handleReset = async () => {
    const fresh = getDefaultState()

    const payload = {
      ...fresh,
      meta: {
        savedAt: new Date().toISOString(),
        clinicId,
        source: "backend"
      }
    }

    setForm(fresh)

    try {
      setSavingBackend(true)

      let backendData = null

      if (clinicId && clinicId !== "default") {
        const { data } = await axios.put("/api/schedule-availability", {
          clinicId,
          hours: payload.hours,
          breaks: payload.breaks,
          availability: payload.availability,
          rules: payload.rules,
          meta: payload.meta
        })

        backendData = data
      }

      const nextSavedAt =
        backendData?.updatedAt ||
        backendData?.meta?.savedAt ||
        payload.meta.savedAt

      const localPayload = {
        ...payload,
        meta: {
          ...payload.meta,
          savedAt: nextSavedAt,
          source: backendData?.source || "local"
        }
      }

      localStorage.setItem(getStorageKey(clinicId), JSON.stringify(localPayload))
      setSavedAt(nextSavedAt)

      window.dispatchEvent(new Event("odontoflow:schedule-availability-updated"))

      setStatusMessage("Se restauró la configuración base correctamente.")
      setStatusTone("warning")
    } catch (err) {
      localStorage.setItem(getStorageKey(clinicId), JSON.stringify(payload))
      setSavedAt(payload.meta.savedAt)

      window.dispatchEvent(new Event("odontoflow:schedule-availability-updated"))

      setStatusMessage(
        "No se pudo restaurar en backend. Se restauró respaldo local temporal."
      )
      setStatusTone("warning")
    } finally {
      setSavingBackend(false)
    }
  }

  const enabledDays = useMemo(
    () => countEnabledDays(form.hours),
    [form.hours]
  )

  const blockedDatesCount = useMemo(
    () => countBlockedDates(form.breaks.blockedDatesText),
    [form.breaks.blockedDatesText]
  )

  return (
    <SettingsModuleScaffold
      eyebrow="Operación de la clínica"
      title="Agenda y disponibilidad"
      subtitle="Centraliza horarios, descansos, bloqueos y reglas base de agenda para dejar de repartir esta lógica entre pantallas sueltas."
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12
        }}
      >
        <MetricCard
          label="Contexto"
          value={clinicId && clinicId !== "default" ? "Clínica seleccionada" : "General"}
          tone="neutral"
        />
        <MetricCard
          label="Días activos"
          value={String(enabledDays)}
          tone="success"
        />
        <MetricCard
          label="Bloqueos cargados"
          value={String(blockedDatesCount)}
          tone="warning"
        />
        <MetricCard
          label="Estado"
          value={loadingBackend ? "Cargando..." : getStatusLabel(savedAt)}
          tone={savedAt ? "info" : "neutral"}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap"
        }}
      >
        <button onClick={() => applyPreset("standard")} style={secondaryButtonStyle}>
          Preset estándar
        </button>
        <button onClick={() => applyPreset("extended")} style={secondaryButtonStyle}>
          Preset extendido
        </button>
        <button onClick={() => applyPreset("compact")} style={secondaryButtonStyle}>
          Preset compacto
        </button>
        <button
          onClick={handleSave}
          disabled={savingBackend || loadingBackend}
          style={{
            ...primaryButtonStyle,
            opacity: savingBackend || loadingBackend ? 0.65 : 1,
            cursor: savingBackend || loadingBackend ? "not-allowed" : "pointer"
          }}
        >
          {savingBackend ? "Guardando..." : "Guardar configuración"}
        </button>

        <button
          onClick={handleReset}
          disabled={savingBackend || loadingBackend}
          style={{
            ...dangerButtonStyle,
            opacity: savingBackend || loadingBackend ? 0.65 : 1,
            cursor: savingBackend || loadingBackend ? "not-allowed" : "pointer"
          }}
        >
          Restaurar base
        </button>
      </div>

      {statusMessage ? (
        <StateCard tone={statusTone} title="Estado">
          {statusMessage}
        </StateCard>
      ) : null}

      {activeTab === "hours" ? (
        <SectionCard
          title="Horarios por día"
          subtitle="Define qué días opera la clínica y en qué ventana base de atención."
        >
          <div style={{ display: "grid", gap: 12 }}>
            {WEEK_DAYS.map((day) => {
              const config = form.hours[day.key]
              return (
                <div
                  key={day.key}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 16,
                    padding: 14,
                    background: "#ffffff",
                    display: "grid",
                    gridTemplateColumns: "minmax(160px, 220px) minmax(0, 1fr)",
                    gap: 12,
                    alignItems: "center"
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 900, color: "#0f172a" }}>{day.label}</div>
                    <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>
                      {config.enabled ? "Disponible para agenda" : "No laborable"}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px repeat(2, minmax(120px, 160px))",
                      gap: 10,
                      alignItems: "center"
                    }}
                  >
                    <label style={switchWrapStyle}>
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) =>
                          updateHours(day.key, "enabled", e.target.checked)
                        }
                      />
                      <span>Activo</span>
                    </label>

                    <div>
                      <FieldLabel>Inicio</FieldLabel>
                      <input
                        type="time"
                        value={config.start}
                        disabled={!config.enabled}
                        onChange={(e) => updateHours(day.key, "start", e.target.value)}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <FieldLabel>Fin</FieldLabel>
                      <input
                        type="time"
                        value={config.end}
                        disabled={!config.enabled}
                        onChange={(e) => updateHours(day.key, "end", e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </SectionCard>
      ) : null}

      {activeTab === "breaks" ? (
        <SectionCard
          title="Bloqueos y descansos"
          subtitle="Controla comida, buffers operativos y fechas bloqueadas."
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 14
            }}
          >
            <CardBlock title="Descanso de comida">
              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.breaks.lunchEnabled}
                  onChange={(e) => updateBreaks("lunchEnabled", e.target.checked)}
                />
                <span>Activar receso de comida</span>
              </label>

              <div style={twoColGridStyle}>
                <div>
                  <FieldLabel>Inicio</FieldLabel>
                  <input
                    type="time"
                    value={form.breaks.lunchStart}
                    disabled={!form.breaks.lunchEnabled}
                    onChange={(e) => updateBreaks("lunchStart", e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <FieldLabel>Fin</FieldLabel>
                  <input
                    type="time"
                    value={form.breaks.lunchEnd}
                    disabled={!form.breaks.lunchEnabled}
                    onChange={(e) => updateBreaks("lunchEnd", e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            </CardBlock>

            <CardBlock title="Buffers operativos">
              <div style={twoColGridStyle}>
                <div>
                  <FieldLabel>Minutos antes</FieldLabel>
                  <input
                    type="number"
                    value={form.breaks.bufferBeforeMinutes}
                    onChange={(e) =>
                      updateBreaks("bufferBeforeMinutes", Number(e.target.value || 0))
                    }
                    style={inputStyle}
                  />
                </div>

                <div>
                  <FieldLabel>Minutos después</FieldLabel>
                  <input
                    type="number"
                    value={form.breaks.bufferAfterMinutes}
                    onChange={(e) =>
                      updateBreaks("bufferAfterMinutes", Number(e.target.value || 0))
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.4 }}>
                Sirve para dejar colchón operativo entre citas o procedimientos largos.
              </div>
            </CardBlock>
          </div>

          <div style={{ marginTop: 14 }}>
            <CardBlock title="Fechas bloqueadas y observaciones">
              <div style={twoColGridStyle}>
                <div>
                  <FieldLabel>Fechas bloqueadas</FieldLabel>
                  <textarea
                    value={form.breaks.blockedDatesText}
                    onChange={(e) => updateBreaks("blockedDatesText", e.target.value)}
                    placeholder={"Una fecha por línea.\nEjemplo:\n2026-05-01\n2026-05-10"}
                    style={textareaStyle}
                  />
                </div>

                <div>
                  <FieldLabel>Notas operativas</FieldLabel>
                  <textarea
                    value={form.breaks.notes}
                    onChange={(e) => updateBreaks("notes", e.target.value)}
                    placeholder="Ej. Bloquear por inventario, capacitación, mantenimiento, etc."
                    style={textareaStyle}
                  />
                </div>
              </div>
            </CardBlock>
          </div>
        </SectionCard>
      ) : null}

      {activeTab === "availability" ? (
        <SectionCard
          title="Disponibilidad operativa"
          subtitle="Reglas base para determinar cómo se ofrece espacio de agenda."
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 14
            }}
          >
            <CardBlock title="Parámetros base">
              <div style={twoColGridStyle}>
                <div>
                  <FieldLabel>Slot base (min)</FieldLabel>
                  <input
                    type="number"
                    value={form.availability.slotMinutes}
                    onChange={(e) =>
                      updateAvailability("slotMinutes", Number(e.target.value || 0))
                    }
                    style={inputStyle}
                  />
                </div>

                <div>
                  <FieldLabel>Ventana de reserva (días)</FieldLabel>
                  <input
                    type="number"
                    value={form.availability.bookingWindowDays}
                    onChange={(e) =>
                      updateAvailability("bookingWindowDays", Number(e.target.value || 0))
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <FieldLabel>Límite de agenda del mismo día</FieldLabel>
                <input
                  type="time"
                  value={form.availability.sameDayCutoffHour}
                  onChange={(e) =>
                    updateAvailability("sameDayCutoffHour", e.target.value)
                  }
                  style={inputStyle}
                />
              </div>
            </CardBlock>

            <CardBlock title="Controles operativos">
              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.availability.respectChairCapacity}
                  onChange={(e) =>
                    updateAvailability("respectChairCapacity", e.target.checked)
                  }
                />
                <span>Respetar capacidad por silla</span>
              </label>

              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.availability.requireActiveDoctor}
                  onChange={(e) =>
                    updateAvailability("requireActiveDoctor", e.target.checked)
                  }
                />
                <span>Requerir doctor activo para ofertar horario</span>
              </label>

              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.availability.allowSameTimeDifferentChair}
                  onChange={(e) =>
                    updateAvailability("allowSameTimeDifferentChair", e.target.checked)
                  }
                />
                <span>Permitir misma hora en distinta silla</span>
              </label>

              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.availability.allowManualOverbook}
                  onChange={(e) =>
                    updateAvailability("allowManualOverbook", e.target.checked)
                  }
                />
                <span>Permitir sobrecupo manual</span>
              </label>
            </CardBlock>
          </div>
        </SectionCard>
      ) : null}

      {activeTab === "rules" ? (
        <SectionCard
          title="Reglas base de agenda"
          subtitle="Define restricciones y alertas generales para que la operación sea más consistente."
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 14
            }}
          >
            <CardBlock title="Duraciones y alertas">
              <div style={twoColGridStyle}>
                <div>
                  <FieldLabel>Duración por default (min)</FieldLabel>
                  <input
                    type="number"
                    value={form.rules.defaultAppointmentMinutes}
                    onChange={(e) =>
                      updateRules("defaultAppointmentMinutes", Number(e.target.value || 0))
                    }
                    style={inputStyle}
                  />
                </div>

                <div>
                  <FieldLabel>Buffer urgencia (min)</FieldLabel>
                  <input
                    type="number"
                    value={form.rules.emergencyBufferMinutes}
                    onChange={(e) =>
                      updateRules("emergencyBufferMinutes", Number(e.target.value || 0))
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.rules.warnWhenDoctorExceeds8Hours}
                  onChange={(e) =>
                    updateRules("warnWhenDoctorExceeds8Hours", e.target.checked)
                  }
                />
                <span>Alertar cuando un doctor exceda 8 horas</span>
              </label>
            </CardBlock>

            <CardBlock title="Reglas de consistencia">
              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.rules.allowBackToBackLongProcedures}
                  onChange={(e) =>
                    updateRules("allowBackToBackLongProcedures", e.target.checked)
                  }
                />
                <span>Permitir procedimientos largos consecutivos</span>
              </label>

              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.rules.requireTreatmentBeforeConfirm}
                  onChange={(e) =>
                    updateRules("requireTreatmentBeforeConfirm", e.target.checked)
                  }
                />
                <span>Requerir tratamiento antes de confirmar cita</span>
              </label>

              <label style={switchWrapStyle}>
                <input
                  type="checkbox"
                  checked={form.rules.autoBlockLunchWhenOpen}
                  onChange={(e) =>
                    updateRules("autoBlockLunchWhenOpen", e.target.checked)
                  }
                />
                <span>Respetar receso de comida cuando esté activado</span>
              </label>
            </CardBlock>
          </div>

          <div style={{ marginTop: 14 }}>
            <CardBlock title="Notas de reglas">
              <FieldLabel>Comentarios del equipo</FieldLabel>
              <textarea
                value={form.rules.notes}
                onChange={(e) => updateRules("notes", e.target.value)}
                placeholder="Ej. No dejar huecos menores a 15 min, reservar mañana para cirugía, etc."
                style={textareaStyle}
              />
            </CardBlock>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard
        title="Atajos relacionados"
        subtitle="Mientras seguimos expandiendo esta área, estos accesos ayudan a cerrar el flujo."
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap"
          }}
        >
          <button
            onClick={() => navigate("/settings/team-access?tab=doctors")}
            style={primaryButtonStyle}
          >
            Ir a doctores
          </button>

          <button
            onClick={() => navigate("/settings/clinic-resources?tab=sillas")}
            style={secondaryButtonStyle}
          >
            Ir a sillas
          </button>

          <button
            onClick={() => navigate("/agenda")}
            style={secondaryButtonStyle}
          >
            Ir a agenda
          </button>
        </div>
      </SectionCard>
    </SettingsModuleScaffold>
  )
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        padding: 18,
        background: "#ffffff",
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em"
          }}
        >
          {title}
        </div>
        {subtitle ? (
          <div
            style={{
              marginTop: 4,
              fontSize: 12.5,
              color: "#64748b",
              lineHeight: 1.45
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function CardBlock({ title, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: 14,
        background: "#f8fafc",
        display: "grid",
        gap: 12
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}

function MetricCard({ label, value, tone = "neutral" }) {
  const palette = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", value: "#166534" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" },
    info: { bg: "#eff6ff", border: "#bfdbfe", value: "#1d4ed8" }
  }

  const visual = palette[tone] || palette.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 18,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.15,
          wordBreak: "break-word"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function FieldLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: 6,
        fontSize: 12,
        color: "#64748b",
        fontWeight: 800
      }}
    >
      {children}
    </label>
  )
}

function StateCard({ tone = "neutral", title, children }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", color: "#334155" },
    info: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" },
    warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        borderRadius: 16,
        padding: 16
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>
      <div style={{ lineHeight: 1.45 }}>{children}</div>
    </div>
  )
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  outline: "none",
  background: "#ffffff",
  boxSizing: "border-box"
}

const textareaStyle = {
  ...inputStyle,
  minHeight: 110,
  resize: "vertical",
  lineHeight: 1.45
}

const switchWrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "#334155",
  fontWeight: 700
}

const twoColGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12
}

const primaryButtonStyle = {
  minHeight: 40,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 13
}

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 13
}

const dangerButtonStyle = {
  minHeight: 40,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1px solid #fecaca",
  background: "#fef2f2",
  color: "#b91c1c",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 13
}